import { getErrorMessage } from '@utils/getErrorMessage';
import { caseChange } from '@utils/helpers';
import fetch from 'isomorphic-unfetch';
import Cookies from 'js-cookie';

// export const lmsRequest = <T = { [x: string]: any }>(url?: string): LMSRequestObject<T> => {

export const lmsRequest = <T>(url?: string): LMSRequestObject<T> => {
  const apiUrl = process.env.NEXT_PUBLIC_IS_QA
    ? 'https://orange-lms-api-qa.herokuapp.com'
    : process.env.NEXT_PUBLIC_API_URL;
  return {
    requestObject: {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },

    body(newBody, stringifyBody) {
      this.requestObject.body = stringifyBody ? JSON.stringify(newBody) : newBody;
      return this;
    },

    post() {
      this.requestObject.method = 'POST';
      return this;
    },

    get() {
      this.requestObject.method = 'GET';
      return this;
    },

    patch() {
      this.requestObject.method = 'PATCH';
      return this;
    },

    put() {
      this.requestObject.method = 'PUT';
      return this;
    },

    delete() {
      this.requestObject.method = 'DELETE';
      return this;
    },

    async send(options) {
      if (options?.logRequest) console.info(`Sending request to ${url}; Object: `, this.requestObject); //eslint-disable-line no-console

      let output;
      try {
        const response = await fetch(`${apiUrl}${url}`, this.requestObject);
        const jsonResponse = response.body ? await response.json() : { status: response.status, body: {} };

        if (options?.enqueueSnackbar) {
          // Display success message if provided
          if (response.ok && options?.successMsg) {
            options.enqueueSnackbar(options.successMsg, {
              variant: 'success',
            });
          }
          // Display error message/messages
          else if (!response.ok) {
            // Display validator error messages
            if (jsonResponse.messages?.length) {
              for (const err of jsonResponse.messages) {
                console.error(err);
                options.enqueueSnackbar(`Error: ${err.message}`, { variant: 'error' });
              }
            }
            // Display error message
            else {
              const errorMsg =
                getErrorMessage(jsonResponse.message) || options?.errorMsg || 'There was an unexpected error';
              // 'NULL' string is used when for omitting the snackbar for certain errors
              if (errorMsg !== 'NULL') {
                options.enqueueSnackbar(errorMsg, {
                  variant: 'error',
                });
              }
            }
          }
        }

        output = {
          ok: response.ok,
          data: jsonResponse.data,
          status: response.status,
          message: jsonResponse.message,
          messages: jsonResponse.messages,
        };
      } catch (error) {
        console.error(error);
        if (options?.enqueueSnackbar) {
          options.enqueueSnackbar('There was an unexpected problem.', {
            variant: 'error',
          });
        }
        output = {
          ok: false,
          data: {},
          status: 500,
        };
      }

      return output;
    },

    noDefaultHeaders() {
      this.requestObject.headers = {};
      return this;
    },

    headers(newHeaders) {
      this.requestObject.headers = { ...this.requestObject.headers, ...newHeaders };
      return this;
    },

    auth(token?: string) {
      this.requestObject.headers['Authorization'] = `Bearer ${
        token || Cookies.get(`${caseChange(Cookies.get('currentRole'))}Token`)
      }`;

      return this;
    },

    accept(newAccept) {
      this.requestObject.headers.Accept = newAccept;
      return this;
    },

    // #Content-Type
    contentType(newContentType) {
      this.requestObject.headers['Content-Type'] = newContentType;
      return this;
    },

    json() {
      this.requestObject.headers['Content-Type'] = 'application/json';
      return this;
    },

    formData() {
      this.requestObject.headers['Content-Type'] = 'multipart/form-data';
      return this;
    },

    urlEncoded() {
      this.requestObject.headers['Content-Type'] = 'application/x-www-form-urlencoded';
      return this;
    },

    // #Helpers
    log() {
      console.info(this.requestObject); //eslint-disable-line no-console
      return this;
    },

    // #Danger Zone
    freelySet(object) {
      this.requestObject = { ...this.requestObject, ...object };
      return this;
    },
  };
};

type LMSRequestObject<T = unknown> = {
  requestObject: {
    method: string;
    headers: { [header: string]: string } | {};
  };
  body: (newBody: object | string, stringifyBody?: boolean) => LMSRequestObject<T>;
  post: () => LMSRequestObject<T>;
  get: () => LMSRequestObject<T>;
  patch: () => LMSRequestObject<T>;
  put: () => LMSRequestObject<T>;
  delete: () => LMSRequestObject<T>;
  send: (options?: {
    logRequest?: true;
    errorMsg?: string;
    successMsg?: string;
    enqueueSnackbar?: any;
  }) => Promise<LMSApiResponse<T>>;
  headers: (newHeaders: object) => LMSRequestObject<T>;
  noDefaultHeaders: () => LMSRequestObject<T>;
  auth: (token?: string) => LMSRequestObject<T>;
  accept: (newAccept: string) => LMSRequestObject<T>;
  contentType: (newContentType: string) => LMSRequestObject<T>;
  json: () => LMSRequestObject<T>;
  formData: () => LMSRequestObject<T>;
  urlEncoded: () => LMSRequestObject<T>;
  log: () => LMSRequestObject<T>;
  freelySet: (object: object) => LMSRequestObject<T> & { [x: string]: any };
};
